import React, { useMemo, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ABOUT_CONTENT, ANIMATION_VARIANTS, HERO_CONTENT } from '../../constants';
import { 
  useMarqueeAnimation,
  useOptimizedBackground,
  usePerformanceMonitoring
} from '../../hooks/useAnimations';
import { handlePageNavigation } from '../../utils/navigation';
import { StyledIcon } from '../animations';

const AboutSection: React.FC = () => {
  usePerformanceMonitoring('AboutSection');
  
  // Mobile detection state
  const [isMobile, setIsMobile] = useState(false);
  
  // Use our new animation hooks
  const { marqueeRef, containerClasses: marqueeContainerClasses, contentClasses: marqueeContentClasses } = useMarqueeAnimation();
  const { backgroundClasses, backgroundStyle } = useOptimizedBackground(
    HERO_CONTENT.backgroundImage,
    'linear-gradient(rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.8))'
  );
  
  // Detect mobile device
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Preload images
  useEffect(() => {
    const imagePaths = [
      ABOUT_CONTENT.image,
      '/assets/images/services/Full_Stack_Icon_1.png',
      '/assets/images/services/Cloud_Icon6_1.png',
      '/assets/images/services/seemless-depolyment_1.png',
      '/assets/images/services/local-expertise_1.png'
    ];
    
    imagePaths.forEach(path => {
      const img = new Image();
      img.src = path;
    });
  }, []);

  // Memoized service items for better performance
  const serviceItems = useMemo(() => [
    {
      id: 'full-stack',
      title: 'Full-Stack Solutions',
      description: 'From compute to code, we handle everything under one roof.',
      icon: '/assets/images/services/Full_Stack_Icon_1.png',
    },
    {
      id: 'scalable-cloud',
      title: 'Scalable Cloud',
      description: 'Flexibility and scalability that grows with your business.',
      icon: '/assets/images/services/Cloud_Icon6_1.png',
    },
    {
      id: 'seamless-deployment',
      title: 'Seamless Deployment',
      description: 'Get your applications to market faster with fully managed deployment services.',
      icon: '/assets/images/services/seemless-depolyment_1.png',
    },
    {
      id: 'local-expertise',
      title: 'Dedicated Local Expertise',
      description: 'Proudly serving Malaysia and Southeast Asia with a team that understands local market dynamics.',
      icon: '/assets/images/services/local-expertise_1.png',
    },
  ], []);

  // Memoized service elements
  const serviceElements = useMemo(() => 
    serviceItems.map((service) => (
      <motion.div
        key={service.id}
        variants={ANIMATION_VARIANTS.slideUp}
        className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl p-6 text-center transition-all duration-300 hover:bg-white/20 hover:shadow-xl will-change-transform transform-gpu"
      >
        <div className="flex justify-center mb-4">
          <StyledIcon 
            src={service.icon}
            alt={service.title}
            size="md"
            color="primary"
            variant="default"
          />
        </div>
        <h4 className="text-lg font-semibold text-white mb-2">{service.title}</h4>
        <p className="text-gray-300 text-sm">{service.description}</p>
      </motion.div>
    )), [serviceItems]
  );

  // Memoized scrolling features
  const scrollingFeatures = useMemo(() => [
    'AI-Powered Solutions', 'Cloud Infrastructure', 'Full-Stack Development',
    'GPU Computing', '24/7 Support', 'Scalable Architecture', 'Local Expertise',
    'Enterprise Grade', 'Custom Solutions', 'Rapid Deployment'
  ], []);

  return (
    <section 
      className="relative section-padding" 
      id="about"
    >
      {/* Optimized background image */}
      <div 
        className={backgroundClasses}
        style={{
          ...backgroundStyle,
          transform: 'translateZ(0) rotate(180deg)',
          backgroundPosition: 'bottom',
        }}
      />
      
      {/* Content Container */}
      <div className="container relative z-40">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          variants={ANIMATION_VARIANTS.staggerChildren}
          className="grid lg:grid-cols-2 gap-12 items-center"
        >
          {/* Content */}
          <motion.div 
            variants={ANIMATION_VARIANTS.slideInLeft}
          >
            <h2 className="heading-1 mb-6">
              {ABOUT_CONTENT.title} 
              <span className='text-primary-gradient'> MTAI</span>
            </h2>
            
            <div className="prose prose-lg text-gray-300 mb-8">
              {ABOUT_CONTENT.description.split('\n\n').map((paragraph, index) => (
                <p key={index} className="mb-4 leading-relaxed font-light">
                  {paragraph}
                </p>
              ))}
            </div>

            <Link
              to="/about"
              className="btn-futuristic inline-flex items-center"
              onClick={handlePageNavigation}
            >
              Learn More About Us
            </Link>
          </motion.div>

          {/* Image */}
          <motion.div 
            variants={ANIMATION_VARIANTS.slideInRight}
            className="relative"
          >
            <div className="aspect-w-4 aspect-h-3 rounded-2xl overflow-hidden shadow-2xl z-50">
              <img
                src={ABOUT_CONTENT.image}
                alt={ABOUT_CONTENT.title}
                className="w-full h-full object-cover will-change-transform transform-gpu"
                loading="lazy"
                decoding="async"
              />
            </div>
            
            {/* Decorative elements with hardware acceleration */}
            <div 
              className="absolute -top-6 -left-6 w-24 h-24 bg-primary-100 rounded-full -z-10 will-change-transform transform-gpu"
            />
            <div 
              className="absolute -bottom-6 -right-6 w-32 h-32 bg-accent-100 rounded-full -z-10 will-change-transform transform-gpu"
            />
          </motion.div>
        </motion.div>

        {/* Key Features */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          variants={ANIMATION_VARIANTS.staggerChildren}
          className="mt-16 pt-16"
        >
          <motion.div 
            variants={ANIMATION_VARIANTS.slideUp} 
            className="text-center mb-12 relative z-30"
          >
            <h3 className="heading-1 mb-6">
              Why Choose 
              <span className='text-primary-gradient'> MTAI?</span>
            </h3>
            <p className="body-text max-w-2xl mx-auto">
              Comprehensive AI and cloud computing solutions tailored to your business needs
            </p>
          </motion.div>

          {/* Services Grid */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            variants={ANIMATION_VARIANTS.staggerChildren}
            className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16"
          >
            {serviceElements}
          </motion.div>

          {/* Optimized Scrolling Features Bar */}
          <motion.div 
            variants={ANIMATION_VARIANTS.slideUp} 
            className="mb-8"
          >
            <div ref={marqueeRef} className={`${marqueeContainerClasses} py-6 bg-dark-800/50 backdrop-blur-sm rounded-lg`}>
              <div 
                className={`${marqueeContentClasses} gap-8`}
                data-marquee-content
                style={{
                  animation: isMobile 
                    ? 'marqueeScrollFast 8s linear infinite'  // Much faster on mobile with GPU acceleration
                    : 'marqueeScroll 25s linear infinite',
                  willChange: 'transform',
                  transform: 'translateZ(0)',  // Force GPU acceleration
                  backfaceVisibility: 'hidden'  // Prevent flickering
                }}
              >
                {scrollingFeatures.map((feature, index) => (
                  <span 
                    key={`feature1-${index}`}
                    className={`inline-block ${isMobile ? 'px-3 py-1 text-sm' : 'px-4 py-2'} text-primary-400 font-medium whitespace-nowrap bg-dark-700/30 rounded-full border border-primary-500/20`}
                  >
                    {feature}
                  </span>
                ))}
                {/* Duplicate for seamless scrolling */}
                {scrollingFeatures.map((feature, index) => (
                  <span 
                    key={`feature2-${index}`}
                    className={`inline-block ${isMobile ? 'px-3 py-1 text-sm' : 'px-4 py-2'} text-primary-400 font-medium whitespace-nowrap bg-dark-700/30 rounded-full border border-primary-500/20`}
                  >
                    {feature}
                  </span>
                ))}
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default React.memo(AboutSection);
